/** A person on a batch: outlined blue on their home line and shift, red when moved in. */
import React, { useCallback, useMemo } from "react";

import { ManualMoves } from "../../controller/use-manual-moves";
import { LaneCard } from "../../utils/lane-rows";
import { pinnedUntilLabel } from "../../utils/pin-range";
import { lbb } from "../../utils/tokens";
import { CardMenu, useCardDrag, useCardHover } from "../manual-controls"; // eslint-disable-line sort-imports

/** A person on a batch, by full name: pick (click), drag to another batch, ⋯ for the rest. Someone from another line or shift is outlined. */
export const PersonChip: React.FC<{
	card: LaneCard;
	away: boolean;
	label: string;
	moves: ManualMoves;
	selected: number | null;
	onSelect: (id: number | null) => void;
	pinnedUntil: string | null;
}> = ({ card, away, label, moves, selected, onSelect, pinnedUntil }) => {
	const { hover, handlers } = useCardHover();
	const item = useMemo(
		() => ({ id: card.id, label, workerId: card.workerId, workerName: card.workerName, workOrderId: card.workOrderId, phaseId: card.phaseId, dayIso: card.dayIso }),
		[card.id, label, card.workerId, card.workerName, card.workOrderId, card.phaseId, card.dayIso]
	);
	const { isDragging, dragRef } = useCardDrag(item, moves);
	const isSelected = selected === card.id;
	const toggle = useCallback(
		(event: React.MouseEvent) => {
			if (selected !== null && !isSelected) return; // let the click reach the batch (hand-off)
			event.stopPropagation();
			onSelect(isSelected ? null : card.id);
		},
		[selected, isSelected, onSelect, card.id]
	);
	const handleUnpin = useCallback(() => void moves.unpin(card.id), [moves, card.id]);
	const handleKeepWeek = useCallback(() => void moves.extend(card.id, "week"), [moves, card.id]);
	const handleKeepUntil = useCallback(() => moves.openUntil(card.id), [moves, card.id]);
	const handleReplace = useCallback(() => moves.replace(card.id), [moves, card.id]);
	const handleRemove = useCallback(() => moves.openRemove(card.id), [moves, card.id]);
	const handleSplit = useCallback(() => void moves.openSplit(card.id), [moves, card.id]);
	const pin = card.isManuallyAssigned ? (pinnedUntil ? ` · ${pinnedUntilLabel(pinnedUntil)}` : " · pinned") : "";
	return (
		<span
			ref={dragRef}
			role="button"
			tabIndex={0}
			title={`${card.workerName}${away ? " · moved in from another line or shift" : " · on their home line"}${pin}`}
			onClick={toggle}
			{...handlers}
			style={{
				display: "inline-flex",
				alignItems: "center",
				gap: 2,
				maxWidth: "100%",
				height: 20,
				padding: "0 4px 0 8px",
				borderRadius: 999,
				boxSizing: "border-box",
				cursor: "grab",
				opacity: isDragging ? 0.35 : 1,
				fontFamily: lbb.fontUi,
				fontSize: 12,
				fontWeight: 600,
				whiteSpace: "nowrap",
				// Outlined, never filled: blue on their home line and shift, red when moved in
				// from elsewhere. The person picked to move gets a heavier outline.
				background: lbb.paper,
				color: away ? lbb.brickDeep : lbb.navy,
				border: `${isSelected ? 2 : 1}px solid ${away ? lbb.brick : lbb.navy}`,
				...(card.isOut ? { border: `1.5px dashed ${lbb.brick}`, color: lbb.brickDeep, textDecoration: "line-through" } : {}),
			}}
		>
			<span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{card.workerName}</span>
			<CardMenu
					pinned={card.isManuallyAssigned}
					visible={hover}
					urgent={Boolean(card.unavailable)}
					onUnpin={handleUnpin}
					onKeepWeek={handleKeepWeek}
					onKeepUntil={handleKeepUntil}
					onReplace={handleReplace}
					onRemove={handleRemove}
					onSplit={handleSplit}
					pinnedUntil={pinnedUntil}
					color={away ? lbb.brickDeep : lbb.navy}
					size={12}
				/>
		</span>
	);
};
